/**
 * The value actions of one value (KPR-1640, KPE-051 ruling i07): what a table
 * cell's menu and the read view's value menu offer for a stored value.
 *
 *   Filter by this value · Exclude this value · Copy value
 *   (Filter by empty · Exclude empty on an empty cell)
 *
 * ONE MODEL FOR BOTH SURFACES. It is built on the filters the page already
 * knows how to write (`valueFilterFor`, `tagFiltersFor`): the same path, the
 * same stored form of a value (an id, not a title; a phone in E.164), the same
 * day pair for a date. Nothing here writes a query. A filter is handed to the
 * page's one door (`applyValueFilter` -> utils/filterValues
 * `applyConditionsToQuery`, the helper the header filters write through), which
 * ANDs it on as a top-level condition.
 *
 * EXCLUDE is the filter's negation, one condition, and it keeps the items with
 * no value: keepr-api compiles `!=`, `!~`, `is none of` on every type as `$nor`
 * over the positive half, and a missing value matches that (SCHEMA.md,
 * "a missing value would match, e.g. `!=`, `is empty`"; compileValues' note on
 * dates, "a `!=` keeps matching blanks"). So "everything but this" needs no
 * extra "or is empty" condition, which would be a nested `or` no chip reads.
 *
 *   eq -> ne · contains -> ncontains · empty -> nempty
 *   a day (`>= day` and `< the next`) -> one `!= day`
 *
 * A VALUE ENTRY is one value of the cell: a Choice that allows several, a lookup
 * that points at several records, several people, several dates and the item's
 * tags each give an entry named by its value; any other cell gives one.
 *
 * What is not a facet offers only Copy and, when the cell is empty, the empty
 * pair: a long text, a rich text, a quantity (no two are ever equal), a file, a
 * repeating group. A masked value offers nothing at all.
 */
import dayjs from 'dayjs';

import { ELEMENT_ID_RE, date as makeDate } from '../../utils/kql';
import { normalizeId } from '../../utils/ids';
import { dayParts } from '../../utils/datePresets';
import { formatDisplayDateTime, formatShortDate } from '../../utils/dateDisplay';
import { canonicalChoices } from '../../utils/choiceValues';
import { isMaskedElement } from '../../utils/maskedElements';
import { extractLookupIds } from '../../utils/lookups';
import { markdownToPlainText } from '../../utils/markdown';
import { entryName } from '../../utils/itemTags';
import { normalizeDateList } from '../../utils/dateOptions';
import { str as makeStr } from '../../utils/kql';
import { isEmptyUserValue, userIdsOf } from '../../utils/userElements';
import { elementLabel, formatValue, getChoiceMeta } from './itemValueDisplay';
import { tagFiltersFor, valueFilterFor } from './itemFilterValues';

export const FILTER_LABEL = 'Filter by this value';
export const EXCLUDE_LABEL = 'Exclude this value';
export const COPY_LABEL = 'Copy value';
export const FILTER_EMPTY_LABEL = 'Filter by empty';
export const EXCLUDE_EMPTY_LABEL = 'Exclude empty';

const NEGATIONS = { eq: 'ne', contains: 'ncontains', empty: 'nempty', nempty: 'empty' };

const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;

/** A day's pair of bounds (`>= day`, `< the next`), as `dayParts` writes it? */
const isDayPair = conditions => Array.isArray(conditions) && conditions.length === 2
	&& conditions[0].cmp === 'gte' && conditions[1].cmp === 'lt'
	&& DAY_RE.test(String(conditions[0].value?.v || ''));

/**
 * The conditions that keep everything BUT these, or null when a condition has
 * no negation to offer.
 */
export function excludeConditions(conditions) {
	if (!Array.isArray(conditions) || !conditions.length) return null;
	if (isDayPair(conditions)) return [{ cmp: 'ne', value: makeDate(conditions[0].value.v) }];
	const out = conditions.map(condition => (NEGATIONS[condition.cmp] ? { ...condition, cmp: NEGATIONS[condition.cmp] } : null));
	return out.every(Boolean) ? out : null;
}

/** The filter's exclusion (same path, negated conditions), or null. */
export function excludeFilter(filter) {
	if (!filter) return null;
	const conditions = excludeConditions(filter.conditions);
	if (!conditions) return null;
	const alternate = filter.alternate ? excludeFilter(filter.alternate) : null;
	return { ...filter, conditions, alternate: alternate || undefined, excluded: true };
}

const withAccumulate = (filter, accumulate) => (filter && accumulate
	? { ...filter, accumulate: true, alternate: filter.alternate ? { ...filter.alternate, accumulate: true } : undefined }
	: filter);

const isBlankValue = value => (
	value === undefined || value === null || value === '' || (Array.isArray(value) && !value.length)
);

/** Is the stored value empty, as the API's `is empty` reads it? */
export function isStoredEmpty(element, value) {
	if (element?.dataType === 'user') return isEmptyUserValue(value);
	return isBlankValue(value);
}

/** A cell of whitespace alone: shown as nothing, stored as something. It offers no action. */
const isWhitespace = value => typeof value === 'string' && value !== '' && !value.trim();

const entryOf = (key, label, include) => ({ key, label, include: withAccumulate(include, false), exclude: excludeFilter(include) });

/** The empty pair for a column: `{ include, exclude }` over the column's own path. */
export function emptyOptions({ path, forceElement = false, aliases = [], label = '', facet = false }) {
	const base = { path, forceElement, facet, aliases, label, valueLabel: 'empty' };
	const include = { ...base, conditions: [{ cmp: 'empty' }] };
	const exclude = { ...base, conditions: [{ cmp: 'nempty' }], excluded: true };
	return { include, exclude };
}

/** The value entries (stored pieces) of an element's value, in the order the cell shows them. */
function entriesOf(element, value) {
	switch (element?.dataType) {
		case 'choice': return Array.isArray(value) ? canonicalChoices(element, value) : [value];
		case 'card-lookup': return extractLookupIds(value);
		case 'user': return userIdsOf(value);
		case 'date':
		case 'date-time':
			return Array.isArray(value) ? normalizeDateList(element.dataType, value) : [value];
		default: return [value];
	}
}

const LIST_CAPABLE = new Set(['choice', 'card-lookup', 'user', 'date', 'date-time']);

/** The text the cell shows for a value, for Copy (never a mask, never an id the pool cannot name). */
export function shownText(element, value, { resolveLookupValue, unitSystem, userNames } = {}) {
	if (isBlankValue(value)) return '';
	switch (element?.dataType) {
		case 'user':
			return userIdsOf(value).map(id => (userNames && userNames[id]) || '').filter(Boolean).join(', ');
		case 'rich-text':
			return markdownToPlainText(String(value)).trim();
		case 'file':
		case 'repeating-group':
			return '';
		default: {
			const meta = getChoiceMeta(element, value);
			return String(meta.label || formatValue(element, value, { unitSystem, resolveLookupValue }) || '').trim();
		}
	}
}

/**
 * The menu for one ELEMENT value.
 *
 *   { entries: [{ key, label, include, exclude }], empty: { include, exclude } | null, copy: string }
 *
 * `column` (the table's `columnFilterOf`) says whether the page can filter on
 * the element at all (`column.field`, the page's vocabulary: a hidden or gone
 * element is not the reader's to filter on) and what its empty pair stands on
 * (`column.kind`: a type with no header filter, a time of day, has none).
 * Without a column (the read view) there is no empty pair. Returns null when the
 * value offers nothing.
 */
export function elementValueMenu({ element, value, column = null, resolveLookupValue = null, facetFor = null, unitSystem = null, userNames = null } = {}) {
	if (!element?.name || isMaskedElement(element) || isWhitespace(value)) return null;
	const copy = shownText(element, value, { resolveLookupValue, unitSystem, userNames });
	if (isStoredEmpty(element, value)) {
		const empty = column && column.field && column.kind
			? emptyOptions({ path: column.path, forceElement: column.forceElement, aliases: column.aliases, label: elementLabel(element) })
			: null;
		return empty ? { entries: [], empty, copy: '' } : null;
	}
	const canFilter = !column || Boolean(column.field);
	const multi = Array.isArray(value) && LIST_CAPABLE.has(element.dataType);
	const entries = [];
	if (canFilter) {
		entriesOf(element, value).forEach((piece, index) => {
			let filter = null;
			let label = '';
			if (element.dataType === 'user') {
				const id = String(piece);
				label = (userNames && userNames[id]) || 'Person';
				const byId = typeof element.id === 'string' && ELEMENT_ID_RE.test(element.id) ? [`#${element.id}`] : [element.name];
				filter = {
					path: column?.path || byId,
					forceElement: column ? column.forceElement : false,
					aliases: column?.aliases || [],
					field: element,
					label: elementLabel(element),
					valueLabel: label,
					conditions: [{ cmp: 'eq', value: makeStr(id) }],
				};
			} else {
				// A reference the pool cannot name is not offered: a menu row
				// reading as an id is no better than none.
				if (element.dataType === 'card-lookup' && typeof resolveLookupValue === 'function' && !resolveLookupValue(piece)) return;
				filter = valueFilterFor({ element, value: piece, resolveLookupValue, facetFor, exact: true });
				label = filter?.valueLabel || '';
			}
			if (!filter) return;
			const entry = entryOf(`${index}`, label, withAccumulate(filter, multi));
			entries.push(entry);
		});
	}
	if (!entries.length && !copy) return null;
	return { entries, empty: null, copy };
}

const SYSTEM_DATE_KEYS = new Set(['sys:createdAt', 'created', 'sys:updatedAt', 'updated']);

/**
 * The menu for one of the item's own columns: Created and Updated (the day),
 * Created by (the account) and Tags (one entry per tag, as the chips name them).
 * `column` is `columnFilterOf`'s answer for the key.
 */
export function systemValueMenu({ key, item, column, userNames = null, tagEntries = [] } = {}) {
	if (!column || !item) return null;
	const canFilter = Boolean(column.field);
	const emptyPair = () => (canFilter && column.kind
		? { entries: [], empty: emptyOptions({ path: column.path, aliases: column.aliases, label: column.label || column.name }), copy: '' }
		: null);
	const base = { path: column.path, forceElement: false, aliases: column.aliases, field: column.field };
	if (SYSTEM_DATE_KEYS.has(key)) {
		const updated = key === 'sys:updatedAt' || key === 'updated';
		const raw = updated ? (item.updatedAt || item.updated_at) : (item.createdAt || item.created_at);
		if (!raw) return emptyPair();
		const copy = formatDisplayDateTime(raw);
		const conditions = canFilter ? dayParts(raw) : null;
		if (!conditions) return copy ? { entries: [], empty: null, copy } : null;
		const label = formatShortDate(raw) || dayjs(raw).format('YYYY-MM-DD');
		const include = { ...base, label: updated ? 'Updated' : 'Created', valueLabel: label, conditions };
		return { entries: [entryOf('0', label, include)], empty: null, copy };
	}
	if (key === 'sys:owner') {
		const id = normalizeId(item.owner) || '';
		if (!id) return emptyPair();
		const label = (userNames && userNames[id]) || 'Person';
		const include = { ...base, label: 'Created by', valueLabel: label, conditions: [{ cmp: 'eq', value: makeStr(id) }] };
		return { entries: canFilter ? [entryOf('0', label, include)] : [], empty: null, copy: (userNames && userNames[id]) || '' };
	}
	if (key === 'sys:tags') {
		const filters = tagFiltersFor(tagEntries);
		// An item holding chips this reader cannot name (a tag not read yet, a
		// deleted one) is not "empty": offer the pair only when there are none.
		if (!filters.length) return tagEntries.length ? null : emptyPair();
		const names = tagEntries.filter(entry => entry && !entry.tombstone && !entry.neutral).map(entryName).filter(Boolean);
		const entries = canFilter
			? filters.map((filter, index) => entryOf(`${index}`, filter.valueLabel, { ...filter, path: column.path, aliases: column.aliases, accumulate: true }))
			: [];
		return { entries, empty: null, copy: names.join(', ') };
	}
	return null;
}

/** Does this model offer anything at all? */
export const hasValueActions = model => Boolean(model && (model.entries.length || model.empty || model.copy));
